import { Badge, Search } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { BodyText } from "@/components/ui/Text";
import { useSearchDrugs } from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useMedications";
import { SearchIcon } from "lucide-react";
import { useState } from "react";
import { useDebounce } from "react-use";
import type { CodifiedMedication } from "./useAmazonVisitIntake";

export const CiqMedicationSearch = ({
  patientId,
  defaultSearchTerm,
  onAdd,
  isPending,
  addedDrug,
  pendingDrugName,
}: {
  patientId: string;
  defaultSearchTerm: string;
  onAdd: (drug: CodifiedMedication) => void;
  isPending: boolean;
  addedDrug?: CodifiedMedication;
  pendingDrugName?: string;
}) => {
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState(defaultSearchTerm);
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 200, [searchTerm]);
  const { isLoading: isSearchLoading, data: searchResults } = useSearchDrugs(
    debouncedSearchTerm,
    patientId,
  );

  const searchOptions =
    searchResults?.map((r) => ({
      label: r.drugName,
      value: r.drugCode,
      metadata: { system: r.drugCodeSystem },
    })) ?? [];

  if (isPending) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 min-h-[34px] border border-transparent rounded-lg">
        <BodyText size="sm">{pendingDrugName}</BodyText>
        <Button
          size="xs"
          type="button"
          isLoading={true}
          className="shrink-0 transition-none bg-button-superclinical text-button-primary border border-ai"
        >
          Add
        </Button>
      </div>
    );
  }

  if (addedDrug) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 min-h-[34px] border border-transparent rounded-lg">
        <BodyText size="sm">{addedDrug.drugName}</BodyText>
        <div className="flex items-center shrink-0" style={{ height: 25 }}>
          <Badge variant="ai">
            <BodyText size="xs">Added</BodyText>
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <Search
        input={searchTerm}
        leftIcon={SearchIcon}
        isLoading={isSearchLoading}
        showLoadingSpinner
        options={searchOptions}
        placeholder="Search to add..."
        onInputChange={(val) => setSearchTerm(val)}
        onItemSelected={(item) =>
          onAdd({
            drugCode: item.value?.toString() ?? "",
            drugName: item.label ?? "",
            drugCodeSystem:
              (item.metadata as { system?: string })?.system ?? "",
          })
        }
      />
    </div>
  );
};
